import React from "react";
import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { Sparkles, Shield, Facebook, Instagram, BadgeCheck } from "lucide-react"; // Zap removed for Facebook review
import { Button } from "components/ui/button";
import { useNavigate } from "react-router-dom";

const Hero = () => {
  const blobRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!blobRef.current) return;

    // GSAP blob animation
    const blob = blobRef.current;
    gsap.to(blob, {
      x: "20vw",
      y: "-10vh",
      scale: 1.2,
      duration: 8,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });

    gsap.to(blob, {
      rotation: 360,
      duration: 20,
      repeat: -1,
      ease: "none",
    });
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero pt-20">
      {/* Animated Background Blob */}
      <div
        ref={blobRef}
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none"
      />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-[100px] pointer-events-none animate-float" />

      <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left: Text Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-left"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">
              Smart Automation
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight"
          >
            Focus on your business, Moujib will Moderate your{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent font-extrabold">
              comments
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="text-xl text-muted-foreground mb-8 leading-relaxed"
          >
            Reply faster, stay on-brand, and keep your comment section clean.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.65, duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2 mb-6"
          >
            <BadgeCheck className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-foreground">
              Verified Meta app
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="flex items-center gap-3 text-sm text-muted-foreground mb-8"
          >
            <span>Works with</span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Facebook className="w-4 h-4" /> Facebook
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Instagram className="w-4 h-4" /> Instagram
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="flex flex-wrap gap-4"
          >
            <Button
              size="lg"
              onClick={() => navigate("/login")}
              className="bg-gradient-primary hover:shadow-glow transition-all duration-300 text-lg px-8"
            >
              Get Started
            </Button>
            <Button
              onClick={() => navigate("/login")}
              size="lg"
              variant="outline"
              className="border-2 border-border hover:border-primary hover:bg-primary/5 text-lg px-8 transition-all duration-300"
            >
              Sign In
            </Button>
          </motion.div>
        </motion.div>

        {/* Right: Feature Highlight Card */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="hidden lg:block"
        >
          <div className="bg-card border border-border rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
            <h3 className="text-2xl font-heading font-bold mb-6">
              Why Moujib?
            </h3>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Smart Moderation</h4>
                  <p className="text-sm text-muted-foreground">
                    Filter spam and toxic comments automatically
                  </p>
                </div>
              </div>

              {/* COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation */}
              {/* <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Instant Auto-Replies</h4>
                  <p className="text-sm text-muted-foreground">
                    Respond to FAQs in seconds
                  </p>
                </div>
              </div> */}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
